import { useEffect, useMemo, useRef, type RefObject } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type DefaultValues } from 'react-hook-form';
import {
    additionalVehicleDetailsSchema,
    type AdditionalVehicleDetailsFormInputs,
} from '../schemas/additionalvehicledetails.schema';
import type { Vehicle } from '../types/vinvrmsearch.types';
import type { VehicleDerivative } from '../types/manualsearch.types';
import {
    buildVehicleIdentity,
    buildDefaultValues,
    buildResetIdentity,
    type SeededVehicleDetails,
} from '../components/AdditionalVehicleDetailsForm/additionalVehicleDetailsFormState';
import { useServiceHistoryPairing } from './useServiceHistoryPairing';
import { useLastServiceMileageEstimate } from './useLastServiceMileageEstimate';

type DirtyFields = Partial<Record<keyof AdditionalVehicleDetailsFormInputs, unknown>>;

function pickDirtyValues(
    values: AdditionalVehicleDetailsFormInputs,
    dirtyFields: DirtyFields,
): Partial<AdditionalVehicleDetailsFormInputs> {
    const dirtyKeys = (Object.keys(dirtyFields) as (keyof AdditionalVehicleDetailsFormInputs)[])
        .filter((field) => dirtyFields[field]);
    return Object.fromEntries(dirtyKeys.map((field) => [field, values[field]]));
}

interface Options {
    selectedVehicle?: Vehicle | VehicleDerivative;
    seededDetails?: SeededVehicleDetails | null;
    prePopulateMileage: boolean;
    clearLastSubmittedRef: RefObject<(() => void) | null>;
}

export function useAdditionalVehicleDetailsFormState({
    selectedVehicle,
    seededDetails,
    prePopulateMileage,
    clearLastSubmittedRef,
}: Options) {
    const vehicleIdentity = useMemo(
        () => buildVehicleIdentity(selectedVehicle),
        [selectedVehicle],
    );

    const defaultValues = useMemo(
        (): DefaultValues<AdditionalVehicleDetailsFormInputs> =>
            buildDefaultValues(selectedVehicle, seededDetails, prePopulateMileage),
        [selectedVehicle, seededDetails, prePopulateMileage],
    );

    // Value-based identity: auto-submit reference churn leaves it unchanged so the accepted mileage is kept;
    // a different vehicle or a newly arrived MOT seed changes it and resets.
    const resetIdentity = useMemo(
        () => buildResetIdentity(vehicleIdentity, seededDetails, prePopulateMileage),
        [vehicleIdentity, seededDetails, prePopulateMileage],
    );

    const {
        handleSubmit,
        control,
        watch,
        formState: { isValid, dirtyFields },
        reset,
        getValues,
        setValue,
    } = useForm<AdditionalVehicleDetailsFormInputs>({
        resolver: zodResolver(additionalVehicleDetailsSchema),
        defaultValues: defaultValues,
        mode: 'onChange',
    });

    useServiceHistoryPairing(watch('lastServiceDate'), watch('lastServiceMileage'), setValue);

    const { lastServiceMileageEstimate, onLastServiceDatePicked } = useLastServiceMileageEstimate({
        lastServiceDate: watch('lastServiceDate'),
        mileage: watch('mileage'),
        annualMileage: watch('annualMileage'),
        getValues,
        setValue,
    });

    // Mirrored so the reset effect reads the current dirty state without joining its deps, which would
    // re-fire the reset on every keystroke.
    const dirtyRef = useRef(dirtyFields);
    dirtyRef.current = dirtyFields;

    const vehicleIdentityRef = useRef(vehicleIdentity);

    // reset() installs the preserved value as the new default, so RHF stops reporting it dirty; this is the
    // durable record of what the user typed across separate config and MOT seed arrivals.
    const editedFieldsRef = useRef<Partial<AdditionalVehicleDetailsFormInputs>>({});

    // Resets on resetIdentity only, not on defaultValues reference churn; defaultValues is read but kept out of deps.
    useEffect(() => {
        // A different vehicle is always a full reset, so one vehicle's edits never carry onto another.
        const vehicleChanged = vehicleIdentityRef.current !== vehicleIdentity;
        vehicleIdentityRef.current = vehicleIdentity;

        // Otherwise keep only user edits; a value equal to its default is never dirty and still takes the seed.
        const current = getValues();
        if (vehicleChanged) {
            editedFieldsRef.current = {};
        } else {
            editedFieldsRef.current = { ...editedFieldsRef.current, ...pickDirtyValues(current, dirtyRef.current) };
        }

        const nextValues = { ...defaultValues, ...editedFieldsRef.current };

        // Skip a no-op reset: reset() cannot remove a field, so it would resurrect a value the user just cleared.
        // The JSON.stringify comparison fails safe: any difference falls through to the reset.
        if (JSON.stringify(current) === JSON.stringify(nextValues)) {
            return;
        }

        reset(nextValues);
        // Bridges the reset's clear-on-reset into useVehicleDetailsAutoSubmit's lastSubmittedData.
        clearLastSubmittedRef.current?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetIdentity, reset]);

    const watchedValues = watch();

    return { control, handleSubmit, watchedValues, isValid, lastServiceMileageEstimate, onLastServiceDatePicked };
}
